import React, { createContext, useCallback, useContext, useState } from "react";
import {
    ArchivePagination,
    ArchiveQuery,
    ArchiveRecord,
    ArchiveSummaryItem,
    emptyArchivePagination,
    RestoreManyResult,
} from "../types/archive";
import {
    getArchiveById,
    getArchives,
    getArchiveSummary,
    purgeArchive,
    restoreArchive,
    restoreArchives,
} from "../api/archiveApi";
import { handleWithAlert } from "../utils/handleWithAlert";
import showAlert from "../common/Alert";

interface ArchiveContextProps {
    records: ArchiveRecord[];
    pagination: ArchivePagination;
    summary: ArchiveSummaryItem[];
    /** Models the API can restore into; anything else is read-only. */
    restorableModels: string[];
    loading: boolean;
    /** True while a restore or a purge is in flight. */
    working: boolean;
    fetchArchives: (query?: ArchiveQuery) => Promise<void>;
    fetchSummary: (query?: ArchiveQuery) => Promise<void>;
    fetchArchiveById: (id: string) => Promise<ArchiveRecord | null>;
    restore: (id: string) => Promise<boolean>;
    restoreSelected: (ids: string[]) => Promise<RestoreManyResult | null>;
    purge: (id: string) => Promise<boolean>;
}

const ArchiveContext = createContext<ArchiveContextProps | undefined>(undefined);

/** The API explains refusals (already restored, id taken, …) — surface that text. */
const serverMessage = (error: any, fallback: string): string =>
    error?.response?.data?.message || error?.message || fallback;

export const ArchiveProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [records, setRecords] = useState<ArchiveRecord[]>([]);
    const [pagination, setPagination] = useState<ArchivePagination>(emptyArchivePagination);
    const [summary, setSummary] = useState<ArchiveSummaryItem[]>([]);
    const [restorableModels, setRestorableModels] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [working, setWorking] = useState(false);

    const fetchArchives = useCallback(async (query: ArchiveQuery = {}) => {
        setLoading(true);
        const result = await handleWithAlert(
            () => getArchives(query),
            { errorMessage: "Failed to fetch archived records" }
        );
        if (result) {
            setRecords(result.data.map((record: any) => ({ ...record, id: record.id || record._id })));
            setPagination(result.pagination);
        }
        setLoading(false);
    }, []);

    // Drives the model filter options, so a failure here must not block the list.
    const fetchSummary = useCallback(async (query: ArchiveQuery = {}) => {
        try {
            const result = await getArchiveSummary(query);
            setSummary(result.summary);
            setRestorableModels(result.restorableModels);
        } catch {
            setSummary([]);
        }
    }, []);

    const fetchArchiveById = useCallback(async (id: string) => {
        return handleWithAlert(
            () => getArchiveById(id),
            { errorMessage: "Failed to fetch the archived record" }
        );
    }, []);

    const restore = useCallback(async (id: string): Promise<boolean> => {
        setWorking(true);
        try {
            await restoreArchive(id);
            await showAlert({
                icon: 'success',
                title: 'Restored',
                text: 'The record was put back successfully.',
            });
            return true;
        } catch (error: any) {
            await showAlert({
                icon: 'error',
                title: 'Restore failed',
                text: serverMessage(error, 'Failed to restore the record'),
            });
            return false;
        } finally {
            setWorking(false);
        }
    }, []);

    const restoreSelected = useCallback(async (ids: string[]): Promise<RestoreManyResult | null> => {
        setWorking(true);
        try {
            const result = await restoreArchives(ids);
            // A bulk restore can partly succeed, so the counts are reported as-is.
            await showAlert({
                icon: result.failed ? 'warning' : 'success',
                title: result.failed ? 'Partly restored' : 'Restored',
                text: `${result.restored} restored, ${result.failed} failed.`,
            });
            return result;
        } catch (error: any) {
            await showAlert({
                icon: 'error',
                title: 'Restore failed',
                text: serverMessage(error, 'Failed to restore the selected records'),
            });
            return null;
        } finally {
            setWorking(false);
        }
    }, []);

    const purge = useCallback(async (id: string): Promise<boolean> => {
        setWorking(true);
        try {
            await purgeArchive(id);
            await showAlert({
                icon: 'success',
                title: 'Deleted',
                text: 'The archived record was permanently deleted.',
            });
            return true;
        } catch (error: any) {
            await showAlert({
                icon: 'error',
                title: 'Delete failed',
                text: serverMessage(error, 'Failed to delete the archived record'),
            });
            return false;
        } finally {
            setWorking(false);
        }
    }, []);

    return (
        <ArchiveContext.Provider value={{
            records,
            pagination,
            summary,
            restorableModels,
            loading,
            working,
            fetchArchives,
            fetchSummary,
            fetchArchiveById,
            restore,
            restoreSelected,
            purge,
        }}>
            {children}
        </ArchiveContext.Provider>
    );
};

export const useArchive = () => {
    const ctx = useContext(ArchiveContext);
    if (!ctx) throw new Error("useArchive must be inside ArchiveProvider");
    return ctx;
};
